import Phaser from "phaser";
import {
  BOARD_COLS, BOARD_ROWS, UI_PANEL_HEIGHT,
  BOARD_IMG_WIDTH, BOARD_IMG_HEIGHT, BOARD_OFFSET_Y,
  GRID_TL, GRID_TR, GRID_BR, GRID_BL,
  GRID2D_SIZE, GRID2D_X, GRID2D_Y, GRID2D_FRAME,
  COLOR_2D_LIGHT_SQUARE, COLOR_2D_DARK_SQUARE, COLOR_2D_FRAME, COLOR_2D_FRAME_EDGE,
  PIECE_2D_REF_H, VIEW_MODE_STORAGE_KEY,
  PIECE_SHEET_FRAME_W,
  TEX_BACKGROUND, TEX_BOARD,
  COLOR_SELECT, COLOR_MOVE_HINT, COLOR_CAPTURE_HINT,
  STALL_PENALTY_TURNS, SUMMON_SICK_ALPHA,
} from "../constants";
import { Board } from "../board";
import { legalMoves, applyMove } from "../moves";
import { maybeWrap, stallPenaltyCount } from "../gameLogic";
import { createRng } from "../rng";
import { StockfishEngine } from "../ai/engine";
import { chooseEnemyMove } from "../ai/enemyAi";
import type { Square, PieceType, Side, Piece, ViewMode } from "../types";
import { requestToken, submitScore, fetchScores } from "../api";
import type { ScoreEntry } from "../api";

const PIECE_TYPES: PieceType[] = ["pawn", "rook", "knight", "bishop", "queen", "king"];
const PIECE_ORIGIN_Y = 0.8;
const BOARD_OVERLAY_ALPHA = 1;

function pieceTextureKey(side: Side, type: PieceType): string {
  return `piece-${side}-${type}`;
}

const PIECE_SYMBOL: Record<PieceType, string> = {
  pawn: "", knight: "N", bishop: "B", rook: "R", queen: "Q", king: "K",
};

function moveToAlgebraic(from: Square, to: Square, piece: Piece, captured: boolean): string {
  const sym = PIECE_SYMBOL[piece.type];
  const toFile = String.fromCharCode(97 + to.col);
  const toRank = (8 - to.row).toString();
  if (piece.type === "pawn" && captured) {
    return `${String.fromCharCode(97 + from.col)}x${toFile}${toRank}`;
  }
  return `${sym}${captured ? "x" : ""}${toFile}${toRank}`;
}

type Phase = "player" | "enemy" | "gameover";

interface PiecePlacement {
  piece: Piece;
  row: number;
  col: number;
}

export class GameScene extends Phaser.Scene {
  private board!: Board;
  private rng!: () => number;
  // Top-left of the (scaled) content block in viewport pixels, and the uniform
  // scale applied to fit the fixed-size content into the current window.
  private contentOriginX = 0;
  private contentOriginY = 0;
  private contentScale = 1;

  // On narrow viewports, scale the board so the playable grid fills BOARD_IMG_WIDTH.
  private boardScale = 1;
  private boardOffsetX = 0;

  // Wraps all game content (board/pieces/UI); positioned + scaled by layout().
  private content!: Phaser.GameObjects.Container;
  private bg!: Phaser.GameObjects.Image;
  private boardLayer!: Phaser.GameObjects.Container;
  private hintLayer!: Phaser.GameObjects.Container;
  private pieceLayer!: Phaser.GameObjects.Container;
  private uiLayer!: Phaser.GameObjects.Container;
  private uiText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private lastEnemyMoveText!: Phaser.GameObjects.Text;

  private selected: Square | null = null;
  private legalForSelected: Square[] = [];
  private phase: Phase = "player";
  private wrapAnimating = false;
  private quitting = false;

  private viewMode: ViewMode = "3d";
  private viewButtonLabel!: Phaser.GameObjects.Text;

  private rowsSurvived = 0;
  private captures = 0;
  // Player moves since the last wrap; drives the anti-camp stall penalty.
  private movesSinceWrap = 0;

  private engine: StockfishEngine | null = null;

  private sessionToken: string | null = null;
  private startTime = 0;
  private capturedEnemyTypes: Record<PieceType, number> = this.emptyCaptures();
  private nameEntry = "";
  private nameDisplay: Phaser.GameObjects.Text | null = null;
  private scoreSubmitted = false;
  private cursorVisible = true;
  private readonly MAX_NAME_LEN = 12;

  constructor() {
    super("GameScene");
  }

  private queueImageIfMissing(key: string, url: string): void {
    if (!this.textures.exists(key)) {
      this.load.image(key, url);
    }
  }

  preload(): void {
    this.queueImageIfMissing(TEX_BACKGROUND, "assets/sprites/background.png");
    this.queueImageIfMissing(TEX_BOARD, "assets/sprites/board.png?v=alpha1");
    for (const side of ["player", "enemy"] as const) {
      for (const type of PIECE_TYPES) {
        const key = pieceTextureKey(side, type);
        this.queueImageIfMissing(key, `assets/pieces/${side}-${type}.png`);
      }
    }
  }

  create(): void {
    this.quitting = false;
    this.phase = "player";
    this.selected = null;
    this.legalForSelected = [];
    this.rowsSurvived = 0;
    this.captures = 0;
    this.movesSinceWrap = 0;
    this.wrapAnimating = false;
    this.sessionToken = (this.scene.settings.data as { token?: string })?.token ?? null;
    this.startTime = Date.now();
    this.capturedEnemyTypes = this.emptyCaptures();
    this.nameEntry = "";
    this.scoreSubmitted = false;
    this.gameOverOverlay?.destroy();
    this.gameOverOverlay = null;
    this.rng = createRng((Math.random() * 0xffffffff) >>> 0);
    this.board = Board.random(this.rng);
    this.viewMode = this.loadViewMode();

    this.computeBoardLayout();
    this.computeHomography();

    // All game content lives in `content`; layout() positions and scales it as a
    // unit so it fits any window size. Child layers stay at the content origin (0,0).
    this.content = this.add.container(0, 0);
    this.boardLayer = this.add.container(0, 0);
    this.hintLayer = this.add.container(0, 0);
    this.pieceLayer = this.add.container(0, 0);
    this.uiLayer = this.add.container(0, 0);
    this.content.add([this.boardLayer, this.hintLayer, this.pieceLayer, this.uiLayer]);

    this.drawBoardBackground();

    // UI panel
    const panelY = BOARD_OFFSET_Y + BOARD_IMG_HEIGHT;
    const panelBg = this.add.rectangle(0, panelY, BOARD_IMG_WIDTH, UI_PANEL_HEIGHT, 0x111114, 0.45).setOrigin(0, 0);
    this.uiLayer.add(panelBg);
    this.uiText = this.add.text(16, panelY + 8, "", { fontFamily: "system-ui, sans-serif", fontSize: "18px", color: "#dddddd" });
    this.statusText = this.add.text(16, panelY + 28, "Your move. Click a piece. Press Q to quit.", { fontFamily: "system-ui, sans-serif", fontSize: "15px", color: "#9ad4ff" });
    this.lastEnemyMoveText = this.add.text(16, panelY + 56, "", { fontFamily: "system-ui, sans-serif", fontSize: "16px", color: "#b48a56" });
    this.uiLayer.add(this.uiText);
    this.uiLayer.add(this.statusText);
    this.uiLayer.add(this.lastEnemyMoveText);

    const quitButtonBg = this.add
      .rectangle(BOARD_IMG_WIDTH - 86, panelY + 68, 132, 34, 0x5a2b2b, 0.95)
      .setStrokeStyle(2, 0xe9cdcd, 0.95)
      .setInteractive({ useHandCursor: true });
    const quitButtonLabel = this.add.text(BOARD_IMG_WIDTH - 86, panelY + 68, "Quit (Q)", {
      fontFamily: "system-ui, sans-serif",
      fontSize: "20px",
      color: "#ffe8e8",
    }).setOrigin(0.5, 0.5);
    this.uiLayer.add(quitButtonBg);
    this.uiLayer.add(quitButtonLabel);

    const viewButtonBg = this.add
      .rectangle(BOARD_IMG_WIDTH - 248, panelY + 68, 152, 34, 0x2b3d5a, 0.95)
      .setStrokeStyle(2, 0xcdd9e9, 0.95)
      .setInteractive({ useHandCursor: true });
    this.viewButtonLabel = this.add.text(BOARD_IMG_WIDTH - 248, panelY + 68, this.viewButtonText(), {
      fontFamily: "system-ui, sans-serif",
      fontSize: "20px",
      color: "#e8f0ff",
    }).setOrigin(0.5, 0.5);
    this.uiLayer.add(viewButtonBg);
    this.uiLayer.add(this.viewButtonLabel);

    viewButtonBg.on("pointerover", () => {
      viewButtonBg.setFillStyle(0x35496f, 0.98);
      this.viewButtonLabel.setScale(1.03);
    });
    viewButtonBg.on("pointerout", () => {
      viewButtonBg.setFillStyle(0x2b3d5a, 0.95);
      this.viewButtonLabel.setScale(1);
    });
    viewButtonBg.on("pointerdown", () => this.toggleViewMode());

    quitButtonBg.on("pointerover", () => {
      quitButtonBg.setFillStyle(0x6f3535, 0.98);
      quitButtonLabel.setScale(1.03);
    });
    quitButtonBg.on("pointerout", () => {
      quitButtonBg.setFillStyle(0x5a2b2b, 0.95);
      quitButtonLabel.setScale(1);
    });
    quitButtonBg.on("pointerdown", () => this.quitToMenu());

    this.input.on("pointerdown", (p: Phaser.Input.Pointer) => this.onPointerDown(p));
    this.input.keyboard?.on("keydown-R", () => {
      if (this.phase !== "gameover") this.scene.restart();
    });
    this.input.keyboard?.on("keydown-Q", () => {
      if (this.phase !== "gameover") this.quitToMenu();
    });
    this.input.keyboard?.on("keydown-V", () => this.toggleViewMode());
    this.input.keyboard?.on("keydown", (event: KeyboardEvent) => {
      if (this.phase !== "gameover" || this.scoreSubmitted) return;
      if (event.key === "Backspace") {
        this.nameEntry = this.nameEntry.slice(0, -1);
        this.updateNameText();
        event.preventDefault();
      } else if (event.key === "Enter") {
        event.preventDefault();
        void this.submitScore();
      } else if (event.key.length === 1 && this.nameEntry.length < this.MAX_NAME_LEN) {
        const char = event.key.toUpperCase();
        this.nameEntry += char;
        this.updateNameText();
        event.preventDefault();
      }
    });

    this.startEngine();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.engine?.dispose());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.engine?.dispose());

    // Reflow whenever the browser window changes size so the canvas keeps
    // filling it. Remove the listener on shutdown to avoid leaking across restarts.
    const onResize = () => this.layout();
    this.scale.on(Phaser.Scale.Events.RESIZE, onResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.scale.off(Phaser.Scale.Events.RESIZE, onResize));

    this.renderPieces();
    this.refreshUi();
    this.layout();
  }

  /** Position + scale all content to fill the current window; cover the background. */
  private layout(): void {
    const vw = this.scale.width;
    const vh = this.scale.height;
    const contentW = BOARD_IMG_WIDTH;
    const contentH = BOARD_OFFSET_Y + BOARD_IMG_HEIGHT + UI_PANEL_HEIGHT;

    this.contentScale = Math.min(vw / contentW, vh / contentH);
    this.contentOriginX = Math.round((vw - contentW * this.contentScale) * 0.5);
    this.contentOriginY = Math.round((vh - contentH * this.contentScale) * 0.5);
    this.content.setPosition(this.contentOriginX, this.contentOriginY).setScale(this.contentScale);

    // Background covers the whole viewport (fills what would otherwise be bars).
    if (this.bg) {
      this.bg.setPosition(vw * 0.5, vh * 0.5);
      const f = this.textures.getFrame(TEX_BACKGROUND);
      if (f && f.width > 0 && f.height > 0) this.bg.setScale(Math.max(vw / f.width, vh / f.height));
      else this.bg.setDisplaySize(vw, vh);
    }
  }

  private startEngine(): void {
    try {
      this.engine = new StockfishEngine();
    } catch (err) {
      // Worker construction failed (e.g. asset missing). Fall back to random AI.
      console.warn("Stockfish engine unavailable, using random AI fallback:", err);
      this.engine = null;
    }
  }

  private loadViewMode(): ViewMode {
    try {
      return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === "2d" ? "2d" : "3d";
    } catch {
      return "3d";
    }
  }

  private viewButtonText(): string {
    return `View: ${this.viewMode.toUpperCase()} (V)`;
  }

  private toggleViewMode(): void {
    if (this.wrapAnimating) return;
    this.viewMode = this.viewMode === "3d" ? "2d" : "3d";
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, this.viewMode);
    } catch {
      // Storage unavailable (private browsing etc.) — the toggle still works for this session.
    }
    this.computeBoardLayout();
    this.computeHomography();
    this.boardLayer.removeAll(true);
    this.drawBoard();
    this.renderPieces();
    this.renderHints();
    this.viewButtonLabel.setText(this.viewButtonText());
  }

  /** Corners of the playable grid in board-image pixel space for the active view. */
  private gridCorners(): {
    tl: { x: number; y: number }; tr: { x: number; y: number };
    br: { x: number; y: number }; bl: { x: number; y: number };
  } {
    if (this.viewMode === "2d") {
      return {
        tl: { x: GRID2D_X, y: GRID2D_Y },
        tr: { x: GRID2D_X + GRID2D_SIZE, y: GRID2D_Y },
        br: { x: GRID2D_X + GRID2D_SIZE, y: GRID2D_Y + GRID2D_SIZE },
        bl: { x: GRID2D_X, y: GRID2D_Y + GRID2D_SIZE },
      };
    }
    return { tl: GRID_TL, tr: GRID_TR, br: GRID_BR, bl: GRID_BL };
  }

  private computeBoardLayout(): void {
    this.boardScale = 1;
    this.boardOffsetX = 0;
    // On narrow viewports (phone/tablet portrait) scale the perspective board so
    // the playable grid fills the full content width (borders overhang off edges).
    // The flat 2D board already spans nearly the full width, so it keeps native scale.
    // This works in board (content-local) space, before the content container's fit
    // scale; window.innerWidth is the actual device width used to detect narrowness.
    if (this.viewMode === "3d" && window.innerWidth < BOARD_IMG_WIDTH) {
      const playableW = GRID_BR.x - GRID_BL.x;
      this.boardScale = BOARD_IMG_WIDTH / playableW;
      this.boardOffsetX = -GRID_BL.x * this.boardScale;
    }
  }



  private drawBoardBackground(): void {
    // Sits behind the content container; sized to cover the viewport in layout().
    this.bg = this.add.image(0, 0, TEX_BACKGROUND).setOrigin(0.5, 0.5).setDepth(-1000);
    this.drawBoard();
  }

  /** Draw the board itself (perspective sprite or flat 2D) into boardLayer. */
  private drawBoard(): void {
    if (this.viewMode === "2d") {
      this.drawBoard2D();
      return;
    }
    const board = this.add.image(this.boardOffsetX, BOARD_OFFSET_Y, TEX_BOARD)
      .setOrigin(0, 0)
      .setAlpha(BOARD_OVERLAY_ALPHA)
      .setScale(this.boardScale);
    // Crop the board so its scaled bottom doesn't draw over the UI panel.
    const panelTop = BOARD_OFFSET_Y + BOARD_IMG_HEIGHT;
    board.setCrop(0, 0, BOARD_IMG_WIDTH, Math.round((panelTop - BOARD_OFFSET_Y) / this.boardScale));
    this.boardLayer.add(board);
  }

  /** Flat top-down board drawn with Graphics (chess.com style) — no artwork needed. */
  private drawBoard2D(): void {
    const tl = this.withBoardOffset({ x: GRID2D_X, y: GRID2D_Y });
    const size = GRID2D_SIZE * this.boardScale;
    const cell = size / BOARD_COLS;
    const frame = GRID2D_FRAME * this.boardScale;

    const g = this.add.graphics();
    // Wooden frame around the playable area.
    g.fillStyle(COLOR_2D_FRAME, 1);
    g.fillRect(tl.x - frame, tl.y - frame, size + frame * 2, size + frame * 2);
    g.lineStyle(3, COLOR_2D_FRAME_EDGE, 1);
    g.strokeRect(tl.x - frame, tl.y - frame, size + frame * 2, size + frame * 2);
    // Squares. (row + col) even is a light square, matching the board artwork.
    for (let r = 0; r < BOARD_ROWS; r++) {
      for (let c = 0; c < BOARD_COLS; c++) {
        g.fillStyle((r + c) % 2 === 0 ? COLOR_2D_LIGHT_SQUARE : COLOR_2D_DARK_SQUARE, 1);
        g.fillRect(tl.x + c * cell, tl.y + r * cell, cell, cell);
      }
    }
    g.lineStyle(2, COLOR_2D_FRAME_EDGE, 0.8);
    g.strokeRect(tl.x, tl.y, size, size);
    this.boardLayer.add(g);

    // File letters along the bottom edge and rank numbers along the left edge,
    // each colored with the opposite square color for contrast.
    const coordStyle = (r: number, c: number) => ({
      fontFamily: "system-ui, sans-serif",
      fontSize: `${Math.round(cell * 0.17)}px`,
      fontStyle: "bold",
      color: (r + c) % 2 === 0 ? "#7e94ad" : "#f0e7d2",
    });
    const pad = cell * 0.06;
    for (let c = 0; c < BOARD_COLS; c++) {
      const label = this.add.text(
        tl.x + (c + 1) * cell - pad,
        tl.y + size - pad,
        String.fromCharCode(97 + c),
        coordStyle(BOARD_ROWS - 1, c),
      ).setOrigin(1, 1);
      this.boardLayer.add(label);
    }
    for (let r = 0; r < BOARD_ROWS; r++) {
      const label = this.add.text(
        tl.x + pad,
        tl.y + r * cell + pad,
        String(BOARD_ROWS - r),
        coordStyle(r, 0),
      ).setOrigin(0, 0);
      this.boardLayer.add(label);
    }
  }

  private withBoardOffset(p: { x: number; y: number }): { x: number; y: number } {
    return {
      x: p.x * this.boardScale + this.boardOffsetX,
      y: p.y * this.boardScale + BOARD_OFFSET_Y,
    };
  }

  // Homography coefficients mapping unit square (u,v in [0,1]) -> board pixels.
  // Computed once from the four GRID corners, scaled by boardScale/boardOffsetX.
  // See Heckbert, "Fundamentals of Texture Mapping and Image Warping" for the closed form.
  private hg!: { a: number; b: number; c: number; d: number; e: number; f: number; g: number; h: number };

  private computeHomography(): void {
    const gc = this.gridCorners();
    const tl = this.withBoardOffset(gc.tl);
    const tr = this.withBoardOffset(gc.tr);
    const br = this.withBoardOffset(gc.br);
    const bl = this.withBoardOffset(gc.bl);
    const dx1 = tr.x - br.x, dx2 = bl.x - br.x;
    const dx3 = tl.x - tr.x + br.x - bl.x;
    const dy1 = tr.y - br.y, dy2 = bl.y - br.y;
    const dy3 = tl.y - tr.y + br.y - bl.y;
    let a: number, b: number, c: number, d: number, e: number, f: number, g: number, h: number;
    if (dx3 === 0 && dy3 === 0) {
      // Affine (parallelogram) case.
      a = tr.x - tl.x; b = bl.x - tl.x; c = tl.x;
      d = tr.y - tl.y; e = bl.y - tl.y; f = tl.y;
      g = 0; h = 0;
    } else {
      const denom = dx1 * dy2 - dy1 * dx2;
      g = (dx3 * dy2 - dx2 * dy3) / denom;
      h = (dx1 * dy3 - dy1 * dx3) / denom;
      a = tr.x - tl.x + g * tr.x;
      b = bl.x - tl.x + h * bl.x;
      c = tl.x;
      d = tr.y - tl.y + g * tr.y;
      e = bl.y - tl.y + h * bl.y;
      f = tl.y;
    }
    this.hg = { a, b, c, d, e, f, g, h };
  }

  private projectUV(u: number, v: number): { x: number; y: number } {
    const { a, b, c, d, e, f, g, h } = this.hg;
    const w = g * u + h * v + 1;
    return { x: (a * u + b * v + c) / w, y: (d * u + e * v + f) / w };
  }

  private squareCenter(row: number, col: number): { x: number; y: number } {
    return this.projectUV((col + 0.5) / BOARD_COLS, (row + 0.5) / BOARD_ROWS);
  }

  private piecePose(row: number, col: number): { x: number; y: number; scale: number } {
    const q = this.cellQuad(row, col);
    if (this.viewMode === "2d") {
      // Center each sprite in its cell so pieces of differing heights (pawns
      // vs. kings) sit vertically centered rather than resting on the bottom.
      const cellH = q.bl.y - q.tl.y;
      const x = (q.tl.x + q.tr.x) * 0.5;
      const y = (q.tl.y + q.bl.y) * 0.5;
      const scale = (cellH * 0.92) / PIECE_2D_REF_H;
      return { x, y, scale };
    }
    const { x, y } = this.squareCenter(row, col);
    const cellW = Math.hypot(q.tr.x - q.tl.x, q.tr.y - q.tl.y);
    const scale = (cellW * 1.0) / PIECE_SHEET_FRAME_W;
    return { x, y, scale };
  }

  private pieceOriginY(): number {
    return this.viewMode === "2d" ? 0.5 : PIECE_ORIGIN_Y;
  }

  private cellQuad(row: number, col: number): {
    tl: { x: number; y: number }; tr: { x: number; y: number };
    br: { x: number; y: number }; bl: { x: number; y: number };
  } {
    return {
      tl: this.projectUV(col / BOARD_COLS, row / BOARD_ROWS),
      tr: this.projectUV((col + 1) / BOARD_COLS, row / BOARD_ROWS),
      br: this.projectUV((col + 1) / BOARD_COLS, (row + 1) / BOARD_ROWS),
      bl: this.projectUV(col / BOARD_COLS, (row + 1) / BOARD_ROWS),
    };
  }

  private renderPieces(): void {
    this.pieceLayer.removeAll(true);
    for (let r = 0; r < BOARD_ROWS; r++) {
      for (let c = 0; c < BOARD_COLS; c++) {
        const piece = this.board.get({ row: r, col: c });
        if (!piece) continue;
        const pose = this.piecePose(r, c);
        const sprite = this.add.image(pose.x, pose.y, pieceTextureKey(piece.side, piece.type))
          .setScale(pose.scale)
          .setAlpha(piece.summonSick ? SUMMON_SICK_ALPHA : 1);
        sprite.setOrigin(0.5, this.pieceOriginY());
        // Depth-sort so pieces in back rows render behind front rows.
        sprite.setDepth(r);
        sprite.setData("pieceId", piece.id);
        this.pieceLayer.add(sprite);
      }
    }
  }

  private snapshotPieces(): Map<number, PiecePlacement> {
    const out = new Map<number, PiecePlacement>();
    for (let r = 0; r < BOARD_ROWS; r++) {
      for (let c = 0; c < BOARD_COLS; c++) {
        const piece = this.board.get({ row: r, col: c });
        if (!piece) continue;
        out.set(piece.id, { piece, row: r, col: c });
      }
    }
    return out;
  }

  private pieceSpritesById(): Map<number, Phaser.GameObjects.Image> {
    const out = new Map<number, Phaser.GameObjects.Image>();
    for (const child of this.pieceLayer.list) {
      if (!(child instanceof Phaser.GameObjects.Image)) continue;
      const pieceId = child.getData("pieceId");
      if (typeof pieceId !== "number") continue;
      out.set(pieceId, child);
    }
    return out;
  }

  private async animateWrapTransition(before: Map<number, PiecePlacement>, spawnedIds: number[]): Promise<void> {
    const after = this.snapshotPieces();
    const spritesById = this.pieceSpritesById();
    const pending: Promise<void>[] = [];

    this.wrapAnimating = true;
    try {
      for (const [pieceId, beforePos] of before) {
        const sprite = spritesById.get(pieceId);
        if (!sprite) continue;
        const afterPos = after.get(pieceId);
        if (!afterPos) {
          pending.push(new Promise((resolve) => {
            this.tweens.add({
              targets: sprite,
              y: sprite.y + 44,
              alpha: 0,
              duration: 170,
              ease: "Quad.In",
              onComplete: () => {
                sprite.destroy();
                resolve();
              },
            });
          }));
          continue;
        }
        if (beforePos.row === afterPos.row && beforePos.col === afterPos.col) continue;
        const pose = this.piecePose(afterPos.row, afterPos.col);
        sprite.setDepth(afterPos.row);
        pending.push(new Promise((resolve) => {
          this.tweens.add({
            targets: sprite,
            x: pose.x,
            y: pose.y,
            scale: pose.scale,
            duration: 220,
            ease: "Sine.InOut",
            onComplete: () => resolve(),
          });
        }));
      }

      const spawnIds = spawnedIds.filter((id) => after.has(id) && !before.has(id));
      pending.push(this.popInPieces(spawnIds));

      if (pending.length > 0) {
        await Promise.all(pending);
      }
    } finally {
      this.wrapAnimating = false;
      this.renderPieces();
    }
  }

  /** Pop freshly-spawned pieces into existence at their board squares (scale + fade in). */
  private async popInPieces(pieceIds: number[]): Promise<void> {
    if (pieceIds.length === 0) return;
    const placements = this.snapshotPieces();
    const pending: Promise<void>[] = [];
    for (const id of pieceIds) {
      const at = placements.get(id);
      if (!at) continue;
      const pose = this.piecePose(at.row, at.col);
      const sprite = this.add.image(pose.x, pose.y, pieceTextureKey(at.piece.side, at.piece.type))
        .setScale(pose.scale * 1.3)
        .setAlpha(0);
      sprite.setOrigin(0.5, this.pieceOriginY());
      sprite.setDepth(at.row);
      sprite.setData("pieceId", id);
      this.pieceLayer.add(sprite);
      pending.push(new Promise((resolve) => {
        this.tweens.add({
          targets: sprite,
          scale: pose.scale,
          alpha: at.piece.summonSick ? SUMMON_SICK_ALPHA : 1,
          duration: 260,
          ease: "Back.Out",
          onComplete: () => resolve(),
        });
      }));
    }
    await Promise.all(pending);
  }

  private renderHints(): void {
    this.hintLayer.removeAll(true);
    if (!this.selected) return;
    const drawQuadOutline = (q: ReturnType<GameScene["cellQuad"]>, color: number) => {
      const g = this.add.graphics();
      g.lineStyle(4, color, 1);
      g.beginPath();
      g.moveTo(q.tl.x, q.tl.y);
      g.lineTo(q.tr.x, q.tr.y);
      g.lineTo(q.br.x, q.br.y);
      g.lineTo(q.bl.x, q.bl.y);
      g.closePath();
      g.strokePath();
      this.hintLayer.add(g);
    };
    drawQuadOutline(this.cellQuad(this.selected.row, this.selected.col), COLOR_SELECT);
    for (const sq of this.legalForSelected) {
      const occ = this.board.get(sq);
      const color = occ ? COLOR_CAPTURE_HINT : COLOR_MOVE_HINT;
      if (occ) {
        drawQuadOutline(this.cellQuad(sq.row, sq.col), color);
      } else {
        const { x, y } = this.squareCenter(sq.row, sq.col);
        const q = this.cellQuad(sq.row, sq.col);
        const cellW = Math.hypot(q.tr.x - q.tl.x, q.tr.y - q.tl.y);
        const dot = this.add.circle(x, y, cellW * 0.18, color, 0.9);
        this.hintLayer.add(dot);
      }
    }
  }

  private pointerToSquare(p: Phaser.Input.Pointer): Square | null {
    // Undo the content container's translate + scale to get board-space coords.
    const localX = (p.x - this.contentOriginX) / this.contentScale;
    const localY = (p.y - this.contentOriginY) / this.contentScale;
    const gc = this.gridCorners();
    const tl = this.withBoardOffset(gc.tl);
    const tr = this.withBoardOffset(gc.tr);
    const br = this.withBoardOffset(gc.br);
    const bl = this.withBoardOffset(gc.bl);
    // Reject if outside the convex quad of the playable area.
    if (!this.pointInQuad(localX, localY, tl, tr, br, bl)) return null;
    // Pick the cell whose center is nearest the pointer.
    let best: Square | null = null;
    let bestD = Infinity;
    for (let r = 0; r < BOARD_ROWS; r++) {
      for (let c = 0; c < BOARD_COLS; c++) {
        const center = this.squareCenter(r, c);
        const dx = center.x - localX;
        const dy = center.y - localY;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = { row: r, col: c }; }
      }
    }
    return best;
  }

  private pointInQuad(
    x: number, y: number,
    a: { x: number; y: number }, b: { x: number; y: number },
    c: { x: number; y: number }, d: { x: number; y: number },
  ): boolean {
    const sign = (px: number, py: number, qx: number, qy: number, rx: number, ry: number) =>
      (px - rx) * (qy - ry) - (qx - rx) * (py - ry);
    // Quad is convex (TL, TR, BR, BL in order). Point must be on the same side of every edge.
    const s1 = sign(x, y, a.x, a.y, b.x, b.y);
    const s2 = sign(x, y, b.x, b.y, c.x, c.y);
    const s3 = sign(x, y, c.x, c.y, d.x, d.y);
    const s4 = sign(x, y, d.x, d.y, a.x, a.y);
    const hasNeg = s1 < 0 || s2 < 0 || s3 < 0 || s4 < 0;
    const hasPos = s1 > 0 || s2 > 0 || s3 > 0 || s4 > 0;
    return !(hasNeg && hasPos);
  }

  private onPointerDown(p: Phaser.Input.Pointer): void {
    if (this.phase !== "player" || this.wrapAnimating) return;
    const sq = this.pointerToSquare(p);
    if (!sq) return;

    if (this.selected) {
      const isLegal = this.legalForSelected.some((s) => s.row === sq.row && s.col === sq.col);
      if (isLegal) {
        const captured = applyMove(this.board, this.selected, sq);
        if (captured) {
          this.captures++;
          if (captured.side === "enemy") {
            this.capturedEnemyTypes[captured.type]++;
          }
        }
        this.selected = null;
        this.legalForSelected = [];
        void this.afterPlayerMove();
        return;
      }
      // Otherwise: maybe selecting a different own piece
      const occ = this.board.get(sq);
      if (occ && occ.side === "player") {
        this.selected = sq;
        this.legalForSelected = legalMoves(this.board, sq);
        this.renderHints();
        return;
      }
      // Deselect
      this.selected = null;
      this.legalForSelected = [];
      this.renderHints();
      return;
    }

    const occ = this.board.get(sq);
    if (occ && occ.side === "player") {
      this.selected = sq;
      this.legalForSelected = legalMoves(this.board, sq);
      this.renderHints();
    }
  }

  private async afterPlayerMove(): Promise<void> {
    this.phase = "enemy";
    // The player has now had their move to react: summoning sickness wears off
    // before any new spawns below get their own sick flag.
    this.board.clearSummonSickness();
    this.renderHints();
    this.renderPieces();
    // Don't count moves toward the stall penalty when enemies sit on the
    // player's back rank — they're physically blocking the wrap, not camping.
    let backRankBlocked = false;
    for (const { sq } of this.board.squaresOf("enemy")) {
      if (sq.row === BOARD_ROWS - 1) { backRankBlocked = true; break; }
    }
    if (!backRankBlocked) {
      this.movesSinceWrap++;
    }
    await this.applyWrapsIfAny(); // resets movesSinceWrap to 0 if the board advances
    if (this.checkGameOver()) return;

    await this.applyStallPenaltyIfAny();

    this.statusText.setText("Enemy thinking...");
    this.refreshUi();
    // Brief delay for readability.
    this.time.delayedCall(400, () => { void this.runEnemyTurn(); });
  }

  /** Punish camping: for every STALL_PENALTY_TURNS moves without a wrap, drop an enemy. */
  private async applyStallPenaltyIfAny(): Promise<void> {
    const n = stallPenaltyCount(this.movesSinceWrap);
    if (n <= 0) return;
    const spawned = this.board.spawnEnemiesAnywhere(this.rng, n);
    if (spawned.length === 0) return;
    this.statusText.setText("⚠ Enemy reinforced — advance to relieve the pressure!");
    await this.popInPieces(spawned.map((p) => p.id));
    this.refreshUi();
  }

  private async runEnemyTurn(): Promise<void> {
    const beforeSprites = this.pieceSpritesById();
    const beforeSnapshot = this.snapshotPieces();

    const action = await chooseEnemyMove(this.board, this.engine, this.rowsSurvived, this.rng);
    if (this.phase !== "enemy") return; // scene may have shut down
    if (action?.captured) this.captures += action.captured.side === "enemy" ? 0 : 1;
    // Note: enemy capturing player piece is a player loss of material, not "captures" counter.

    if (action) {
      // Find the sprite of the piece that moved
      const movedEntry = Array.from(beforeSnapshot.entries()).find(
        ([, p]) => p.row === action.from.row && p.col === action.from.col,
      );
      if (movedEntry) {
        const [movedId] = movedEntry;
        const sprite = beforeSprites.get(movedId);
        if (sprite) {
          // Remove captured piece sprite
          if (action.captured) {
            const capturedEntry = Array.from(beforeSnapshot.entries()).find(
              ([, p]) => p.row === action.to.row && p.col === action.to.col,
            );
            if (capturedEntry) {
              beforeSprites.get(capturedEntry[0])?.destroy();
            }
          }
          const dest = this.piecePose(action.to.row, action.to.col);
          await new Promise<void>((resolve) => {
            this.tweens.add({
              targets: sprite,
              x: dest.x,
              y: dest.y,
              scale: dest.scale,
              duration: 400,
              ease: "Sine.InOut",
              onComplete: () => resolve(),
            });
          });
        }
      }

      // Show move notation
      const movedPiece = this.board.get(action.to);
      if (movedPiece) {
        this.lastEnemyMoveText.setText(
          `Enemy: ${moveToAlgebraic(action.from, action.to, movedPiece, !!action.captured)}`,
        );
      }
    }

    this.renderPieces();
    await this.applyWrapsIfAny();
    if (this.checkGameOver()) return;
    this.phase = "player";
    this.statusText.setText("Your move.");
    this.refreshUi();
  }

  private async applyWrapsIfAny(): Promise<void> {
    // A move may free the bottom row exactly once; loop just in case future rules cascade.
    let guard = 0;
    while (guard++ < BOARD_ROWS) {
      const before = this.snapshotPieces();
      const res = maybeWrap(this.board, this.rng);
      if (!res.wrapped) break;
      this.rowsSurvived++;
      this.movesSinceWrap = 0;
      await this.animateWrapTransition(before, res.spawned.map((piece) => piece.id));
    }
  }

  private gameOverOverlay: Phaser.GameObjects.Container | null = null;

  // ── Scoring helpers ──────────────────────────────────────────────

  private emptyCaptures(): Record<PieceType, number> {
    return { pawn: 0, knight: 0, bishop: 0, rook: 0, queen: 0, king: 0 };
  }

  private computeScore(): { pieceScore: number; rowScore: number; timeBonus: number; total: number } {
    const PIECE_VALUES: Record<PieceType, number> = { pawn: 1, knight: 3, bishop: 3, rook: 5, queen: 9, king: 0 };
    const timeSeconds = (Date.now() - this.startTime) / 1000;
    const pieceScore = (Object.entries(this.capturedEnemyTypes) as [PieceType, number][])
      .reduce((sum, [type, count]) => sum + PIECE_VALUES[type] * count, 0);
    const rowScore = this.rowsSurvived * 10;
    const timeBonus = Math.floor(timeSeconds / 5);
    return { pieceScore, rowScore, timeBonus, total: pieceScore + rowScore + timeBonus };
  }

  // ── Name entry ───────────────────────────────────────────────────

  private updateNameText(): void {
    if (!this.nameDisplay) return;
    const display = this.nameEntry + (this.cursorVisible ? "|" : " ");
    this.nameDisplay.setText(display);
  }

  // ── Score submission ──────────────────────────────────────────────

  private submitting = false;

  private async submitScore(): Promise<void> {
    if (this.submitting || this.scoreSubmitted || !this.nameEntry.trim()) return;
    this.submitting = true;
    const score = this.computeScore();
    const timeSeconds = (Date.now() - this.startTime) / 1000;

    if (!this.sessionToken) {
      this.showSubmitError("Server unavailable");
      this.submitting = false;
      return;
    }

    const res = await submitScore(
      this.sessionToken,
      this.nameEntry.trim(),
      score.total,
      this.rowsSurvived,
      this.captures,
      timeSeconds,
    );

    if (res.ok) {
      this.scoreSubmitted = true;
      this.showLeaderboard(res.rank ?? 0);
    } else {
      this.showSubmitError(res.error ?? "Submit failed");
    }
    this.submitting = false;
  }

  private submitErrorText: Phaser.GameObjects.Text | null = null;

  private showSubmitError(msg: string): void {
    if (this.submitErrorText) {
      this.submitErrorText.setText(msg);
      return;
    }
    this.submitErrorText = this.add.text(0, 0, msg, {
      fontFamily: "system-ui, sans-serif", fontSize: "16px", color: "#ff6b6b",
    }).setOrigin(0.5, 0.5);
    this.submitErrorText.setPosition(this.contentWidth() * 0.5, this.contentHeight() * 0.5 + 130);
    this.gameOverOverlay?.add(this.submitErrorText);
  }

  private contentWidth(): number {
    return BOARD_IMG_WIDTH;
  }

  private contentHeight(): number {
    return BOARD_OFFSET_Y + BOARD_IMG_HEIGHT + UI_PANEL_HEIGHT;
  }

  // ── Leaderboard display ──────────────────────────────────────────

  private leaderboardText: Phaser.GameObjects.Text | null = null;
  private rankText: Phaser.GameObjects.Text | null = null;

  private async showLeaderboard(playerRank: number): Promise<void> {
    if (this.nameDisplay) { this.nameDisplay.destroy(); this.nameDisplay = null; }
    if (this.submitErrorText) { this.submitErrorText.destroy(); this.submitErrorText = null; }

    const cx = this.contentWidth() * 0.5;
    const cy = this.contentHeight() * 0.5;
    const score = this.computeScore();

    if (this.rankText) this.rankText.destroy();
    this.rankText = this.add.text(cx, cy + 50, `Rank: #${playerRank}  —  Score: ${score.total}`, {
      fontFamily: "system-ui, sans-serif", fontSize: "22px", color: "#f0d8a0",
    }).setOrigin(0.5, 0.5);
    this.gameOverOverlay?.add(this.rankText);

    if ((this.gameOverOverlay as any)?._nameLabel) {
      (this.gameOverOverlay as any)._nameLabel.destroy();
      (this.gameOverOverlay as any)._nameLabel = null;
    }

    if (this.leaderboardText) this.leaderboardText.destroy();

    try {
      const data = await fetchScores();
      this.renderLeaderboard(data.scores, playerRank);
    } catch {
      this.renderLeaderboard([], playerRank);
    }
  }

  private renderLeaderboard(scores: ScoreEntry[], _playerRank: number): void {
    const cx = this.contentWidth() * 0.5;
    const cy = this.contentHeight() * 0.5;

    const lines = scores.length === 0
      ? ["  (no scores yet)"]
      : scores.map((s, i) => {
          const rank = `${i + 1}.`.padEnd(3);
          const name = s.name.padEnd(12);
          return `  ${rank} ${name} ${s.score}`;
        });

    const overlay = this.gameOverOverlay as any;

    if (overlay?._leaderboardTitle) {
      overlay._leaderboardTitle.destroy();
      overlay._leaderboardTitle = null;
    }
    if (this.leaderboardText) this.leaderboardText.destroy();

    const title = this.add.text(cx, cy + 85, "── TOP 10 ──", {
      fontFamily: "monospace",
      fontSize: "18px",
      color: "#b48a56",
    }).setOrigin(0.5, 0);
    this.gameOverOverlay?.add(title);
    if (overlay) overlay._leaderboardTitle = title;

    const entriesY = cy + 85 + title.height + 8;
    this.leaderboardText = this.add.text(cx, entriesY, lines.join("\n"), {
      fontFamily: "monospace",
      fontSize: "18px",
      color: "#eef2f7",
      lineSpacing: 4,
    }).setOrigin(0.5, 0);
    this.gameOverOverlay?.add(this.leaderboardText);

    const panelBg = overlay?._panelBg as Phaser.GameObjects.Rectangle | undefined;
    if (!panelBg || !overlay) return;

    const oldPanelH: number = overlay._panelH;
    const leaderboardBottom = entriesY + this.leaderboardText.height;
    const panelTop = cy - oldPanelH * 0.5;
    const newPanelH = (leaderboardBottom - panelTop) + 90;
    if (newPanelH <= oldPanelH) return;

    const extra = newPanelH - oldPanelH;
    panelBg.setSize(560, newPanelH);
    panelBg.setPosition(cx, cy + extra * 0.5);

    const newBY = (overlay._buttonY as number) + extra;
    (overlay._submitBg as Phaser.GameObjects.Rectangle)?.setY(newBY);
    (overlay._submitLabel as Phaser.GameObjects.Text)?.setY(newBY);
    (overlay._restartBg as Phaser.GameObjects.Rectangle)?.setY(newBY);
    (overlay._restartLabel as Phaser.GameObjects.Text)?.setY(newBY);
    (overlay._quitBg as Phaser.GameObjects.Rectangle)?.setY(newBY);
    (overlay._quitLabel as Phaser.GameObjects.Text)?.setY(newBY);
  }

  // ── Game Over Overlay ────────────────────────────────────────────

  private showGameOverOverlay(): void {
    this.gameOverOverlay?.destroy();
    this.nameDisplay = null;
    this.submitErrorText = null;
    this.leaderboardText = null;
    this.rankText = null;
    this.cursorVisible = true;
    this.scoreSubmitted = false;

    const contentW = this.contentWidth();
    const contentH = this.contentHeight();
    const cx = contentW * 0.5;
    const cy = contentH * 0.5;
    const PANEL_W = 560;
    const PANEL_H = 510;

    const overlay = this.add.container(0, 0);
    overlay.setDepth(10000);
    this.content.add(overlay);

    overlay.add(this.add.rectangle(cx, cy, contentW, contentH, 0x000000, 0.6));
    const panelBg = this.add.rectangle(cx, cy, PANEL_W, PANEL_H, 0x101116, 0.94)
      .setStrokeStyle(2, 0xb48a56, 0.9);
    overlay.add(panelBg);
    (overlay as any)._panelBg = panelBg;
    (overlay as any)._panelH = PANEL_H;

    overlay.add(
      this.add.text(cx, cy - PANEL_H * 0.5 + 42, "GAME OVER", {
        fontFamily: "system-ui, sans-serif", fontSize: "44px", color: "#f0d8a0",
      }).setOrigin(0.5, 0.5),
    );

    const score = this.computeScore();
    const pieceDetails = (Object.entries(this.capturedEnemyTypes) as [PieceType, number][])
      .filter(([, n]) => n > 0)
      .map(([type, n]) => `${type} x${n}`)
      .join(", ");

    const labelStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: "system-ui, sans-serif", fontSize: "20px", color: "#aab2c4",
    };
    const valueStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: "system-ui, sans-serif", fontSize: "20px", color: "#eef2f7",
    };
    const LABEL_X = cx - 160;
    const VALUE_X = cx + 160;
    const ROW_H = 32;

    let y = cy - 170;

    overlay.add(this.add.text(cx, y, "Score Breakdown", {
      fontFamily: "system-ui, sans-serif", fontSize: "18px", color: "#b48a56", fontStyle: "bold",
    }).setOrigin(0.5, 0.5));
    y += 32;

    // Pieces captured
    overlay.add(this.add.text(LABEL_X, y, "Pieces captured", labelStyle).setOrigin(0, 0.5));
    overlay.add(this.add.text(VALUE_X, y, `${score.pieceScore}`, valueStyle).setOrigin(1, 0.5));
    if (pieceDetails) {
      y += 20;
      overlay.add(this.add.text(LABEL_X + 16, y, pieceDetails, {
        fontFamily: "system-ui, sans-serif", fontSize: "12px", color: "#7a8aa0",
      }).setOrigin(0, 0.5));
    }
    y += ROW_H;

    // Rows survived
    overlay.add(this.add.text(LABEL_X, y, "Rows survived", labelStyle).setOrigin(0, 0.5));
    overlay.add(this.add.text(VALUE_X, y, `${score.rowScore}`, valueStyle).setOrigin(1, 0.5));
    y += ROW_H;

    // Time bonus
    overlay.add(this.add.text(LABEL_X, y, "Time bonus", labelStyle).setOrigin(0, 0.5));
    overlay.add(this.add.text(VALUE_X, y, `+${score.timeBonus}`, valueStyle).setOrigin(1, 0.5));
    y += ROW_H;

    // Divider
    y += 12;
    overlay.add(this.add.rectangle(cx, y, PANEL_W - 80, 1, 0xb48a56, 0.5));
    y += 22;

    // Total
    overlay.add(this.add.text(LABEL_X, y, "TOTAL", {
      fontFamily: "system-ui, sans-serif", fontSize: "26px", color: "#f0d8a0", fontStyle: "bold",
    }).setOrigin(0, 0.5));
    overlay.add(this.add.text(VALUE_X, y, `${score.total}`, {
      fontFamily: "system-ui, sans-serif", fontSize: "26px", color: "#f0d8a0", fontStyle: "bold",
    }).setOrigin(1, 0.5));
    y += 48;

    // Name entry label
    const nameLabel = this.add.text(cx, y, "ENTER YOUR NAME", {
      fontFamily: "system-ui, sans-serif", fontSize: "16px", color: "#8a9aba",
    }).setOrigin(0.5, 0.5);
    overlay.add(nameLabel);
    (overlay as any)._nameLabel = nameLabel;
    y += 44;

    // Name input
    this.nameDisplay = this.add.text(cx, y, "", {
      fontFamily: "monospace",
      fontSize: "28px",
      color: "#f0d8a0",
      backgroundColor: "#1a1a24",
      padding: { x: 10, y: 6 },
      fixedWidth: 260,
    }).setOrigin(0.5, 0.5);
    overlay.add(this.nameDisplay);
    this.updateNameText();

    // Cursor blink
    this.cursorVisible = true;
    this.time.addEvent({
      delay: 500,
      loop: true,
      callback: () => {
        if (this.phase !== "gameover" || this.scoreSubmitted) return;
        this.cursorVisible = !this.cursorVisible;
        this.updateNameText();
      },
    });

    // Buttons anchored to panel bottom
    const buttonY = cy + PANEL_H * 0.5 - 52;
    (overlay as any)._buttonY = buttonY;
    const buttonH = 42;
    const gap = 12;
    const subW = 130;
    const rstW = 130;
    const qW = 100;
    const totalW = subW + gap + rstW + gap + qW;
    const startX = cx - totalW * 0.5;

    // Submit button
    const submitBg = this.add.rectangle(startX + subW * 0.5, buttonY, subW, buttonH, 0x2d6f8a, 1)
      .setStrokeStyle(2, 0xade8f4, 0.95)
      .setInteractive({ useHandCursor: true });
    overlay.add(submitBg);
    const submitLabel = this.add.text(startX + subW * 0.5, buttonY, "Submit", {
      fontFamily: "system-ui, sans-serif", fontSize: "20px", color: "#e6f8fa",
    }).setOrigin(0.5, 0.5);
    overlay.add(submitLabel);
    (overlay as any)._submitBg = submitBg;
    (overlay as any)._submitLabel = submitLabel;
    submitBg.on("pointerover", () => submitBg.setFillStyle(0x3587a5, 1));
    submitBg.on("pointerout", () => submitBg.setFillStyle(0x2d6f8a, 1));
    submitBg.on("pointerdown", () => void this.submitScore());

    // Restart button
    const restartBg = this.add.rectangle(startX + subW + gap + rstW * 0.5, buttonY, rstW, buttonH, 0x2d7f4d, 1)
      .setStrokeStyle(2, 0xe6f8dd, 0.95)
      .setInteractive({ useHandCursor: true });
    overlay.add(restartBg);
    const restartLabel = this.add.text(startX + subW + gap + rstW * 0.5, buttonY, "Restart", {
      fontFamily: "system-ui, sans-serif", fontSize: "20px", color: "#f4fff6",
    }).setOrigin(0.5, 0.5);
    overlay.add(restartLabel);
    (overlay as any)._restartBg = restartBg;
    (overlay as any)._restartLabel = restartLabel;
    restartBg.on("pointerover", () => restartBg.setFillStyle(0x35945a, 1));
    restartBg.on("pointerout", () => restartBg.setFillStyle(0x2d7f4d, 1));
    restartBg.on("pointerdown", async () => {
      const tokenRes = await requestToken().catch(() => null);
      this.scene.restart({ token: tokenRes?.token ?? null });
    });

    // Quit button
    const quitBg = this.add.rectangle(startX + subW + gap + rstW + gap + qW * 0.5, buttonY, qW, buttonH, 0x5a2b2b, 1)
      .setStrokeStyle(2, 0xe9cdcd, 0.95)
      .setInteractive({ useHandCursor: true });
    overlay.add(quitBg);
    const quitLabel = this.add.text(startX + subW + gap + rstW + gap + qW * 0.5, buttonY, "Quit", {
      fontFamily: "system-ui, sans-serif", fontSize: "20px", color: "#ffe8e8",
    }).setOrigin(0.5, 0.5);
    overlay.add(quitLabel);
    (overlay as any)._quitBg = quitBg;
    (overlay as any)._quitLabel = quitLabel;
    quitBg.on("pointerover", () => quitBg.setFillStyle(0x6f3535, 1));
    quitBg.on("pointerout", () => quitBg.setFillStyle(0x5a2b2b, 1));
    quitBg.on("pointerdown", () => this.quitToMenu());

    this.gameOverOverlay = overlay;
  }

  private checkGameOver(): boolean {
    if (this.board.countPieces("player") === 0) {
      this.triggerGameOver();
      return true;
    }
    for (const { sq } of this.board.squaresOf("player")) {
      if (legalMoves(this.board, sq).length > 0) return false;
    }
    this.triggerGameOver();
    return true;
  }

  private triggerGameOver(): void {
    this.phase = "gameover";
    this.statusText.setText("");
    this.refreshUi();
    this.gameOverOverlay?.destroy();
    this.gameOverOverlay = null;
    this.time.delayedCall(600, () => this.gameOverOverlay ?? this.showGameOverOverlay());
  }

  private quitToMenu(): void {
    if (this.quitting) return;
    this.quitting = true;
    this.phase = "gameover";
    this.selected = null;
    this.legalForSelected = [];
    this.renderHints();
    this.engine?.dispose();
    this.engine = null;
    this.gameOverOverlay?.destroy();
    this.gameOverOverlay = null;
    this.scene.start("StartScene");
  }

  private refreshUi(): void {
    const p = this.board.countPieces("player");
    const e = this.board.countPieces("enemy");
    let line = `Rows survived: ${this.rowsSurvived}   Captures: ${this.captures}   You: ${p}   Enemy: ${e}`;
    // Warn when the next non-advancing move will trigger an enemy reinforcement.
    const untilPenalty = STALL_PENALTY_TURNS - (this.movesSinceWrap % STALL_PENALTY_TURNS);
    if (this.movesSinceWrap > 0 && untilPenalty === 1) {
      line += "   ⚠ Advance or the enemy reinforces!";
    }
    this.uiText.setText(line);
  }
}
